import tenantConstants from '@constants';
import tenantRoutes from '@routes';
import tenantTheme from '@theme';
import { Badge, Button, notification } from 'antd';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../common';
import { leadNudgeClickEvent, leadNudgeCloseEvent } from '../../services/analyticsService';
import {
  isBayutMatchNudgeSuppressed,
  NUDGE_KEY_BAYUT_MATCH,
  resolveNudgeRedirectionUrl,
  suppressNudgeForPeriod,
} from '../../utility/nudgeSuppression';
import './lead-nudge-toasts.css';

const BAYUT_MATCH_TOAST_KEY = 'leadNudge_bayut_match_toast';

/**
 * TruLeads / Bayut Match nudge from GET /api/users/current `nudges`. Suppresses via cookie until `expires_at`.
 */
const LeadNudgeToasts = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state.app.loginUser) || {};
  const { rtl } = useSelector((state) => state.app.AppConfig) || {};
  const navigate = useNavigate();
  const lastShownKeyRef = useRef(null);
  /** When true, `notification.destroy` after CTA will run `onClose` — skip firing `close_nudge`. */
  const skipCloseNudgeAnalyticsRef = useRef(false);

  const getNudgeAnalyticsContext = () => ({
    language: i18n.language,
    pageTitle: typeof document !== 'undefined' ? document.title : '',
    value: 'Bayut Match',
  });

  useEffect(() => {
    if (!user?.id || !tenantConstants.LEAD_NUDGE_TOAST_ENABLED || !tenantConstants.BAYUT_MATCHING_LEAD) {
      return;
    }
    const nudge = user.parsedNudges?.bayutMatch;
    if (!nudge || nudge.count <= 0 || !nudge.expiresAt) return;
    if (nudge.expiresAt.getTime() <= Date.now()) return;
    if (isBayutMatchNudgeSuppressed(user.id, NUDGE_KEY_BAYUT_MATCH, nudge)) return;

    const dedupe = `${user.id}-${nudge.expiresAt.getTime()}-${nudge.count}`;
    if (lastShownKeyRef.current === dedupe) return;
    lastShownKeyRef.current = dedupe;

    const body =
      nudge.count === 1
        ? t('LeadNudgeTruLeadsToast_singular')
        : t('LeadNudgeTruLeadsToast_plural', { count: nudge.count });

    const leadsPath = user?.is_lms_enabled
      ? tenantRoutes.app('', false, user)?.leads_dashboard?.path
      : null;

    const getTarget = () => resolveNudgeRedirectionUrl(nudge, i18n.language) || leadsPath;

    const goToLeads = () => {
      const target = getTarget();
      skipCloseNudgeAnalyticsRef.current = true;
      leadNudgeClickEvent(user, getNudgeAnalyticsContext());
      notification.destroy(BAYUT_MATCH_TOAST_KEY);
      suppressNudgeForPeriod(user.id, NUDGE_KEY_BAYUT_MATCH, nudge);
      if (!target) return;
      if (target.startsWith('http://') || target.startsWith('https://')) {
        window.location.assign(target);
      } else {
        navigate(target);
      }
    };

    const onClose = () => {
      suppressNudgeForPeriod(user.id, NUDGE_KEY_BAYUT_MATCH, nudge);
      const skipClose = skipCloseNudgeAnalyticsRef.current;
      skipCloseNudgeAnalyticsRef.current = false;
      if (!skipClose) {
        leadNudgeCloseEvent(user, getNudgeAnalyticsContext());
      }
    };

    const cta = getTarget() ? (
      <Button
        type="link"
        className="lead-nudge-truleads-toast__cta"
        onClick={goToLeads}
        style={{ color: tenantTheme['primary-color'] || '#009688' }}
      >
        {t('LeadNudgeViewLeadsCta')}
      </Button>
    ) : null;

    notification.open({
      key: BAYUT_MATCH_TOAST_KEY,
      className: 'lead-nudge-truleads-toast',
      message: <p className="lead-nudge-truleads-toast__text">{body}</p>,
      description: cta,
      icon: (
        <div className="lead-nudge-truleads-toast__icon-wrap">
          <div className="lead-nudge-truleads-toast__icon">
            <Icon icon="BsStars" size="22px" />
          </div>
          <Badge
            count={nudge.count}
            className="lead-nudge-truleads-toast__badge"
            color="#1a8f7a"
            overflowCount={99}
          />
        </div>
      ),
      placement: rtl ? 'bottomLeft' : 'bottomRight',
      duration: 0,
      onClose,
    });
    suppressNudgeForPeriod(user.id, NUDGE_KEY_BAYUT_MATCH, nudge);
  }, [user, t, navigate, i18n.language, rtl]);

  return null;
};

export default LeadNudgeToasts;
